import { Program, ProgramV2, Trailer } from 'frontend/types'
import { Plugin } from '../../types'
import abortStreamListener from './abortStreamListener'
import EventHandlerAdapter from './EventHandlers/EventHandlerAdapter'
import EventHandlers from './EventHandlers/EventHandlers'
import DataBuilder, {
  GTMProgramMetadata,
} from './EventPayloadFactory/DataBuilder'
import {
  AUTOPLAY_ABORT,
  AUTOPLAY_CLICK_PLAY,
  EventData,
  EventPayloadFactory,
} from './EventPayloadFactory/EventPayloadFactory'
import rewindListener from './rewindListener'
import timeEventListener from './timeEventListener'

const STEP_BACK_BTN_SELECTOR =
  '.jw-icon.jw-icon-inline.jw-button-color.jw-reset.jw-icon-rewind'
const MOBILE_STEP_BACK_BTN_SELECTOR =
  '.jw-display-icon-container.jw-display-icon-rewind'

const STEP_FORWARD_BTN_SELECTOR = '.scrub-forward-button'
const MOBILE_STEP_FORWARD_BTN_SELECTOR = '.mobile-scrub-forward-button'

const CHAPTER_BUTTON = '.chapters-button-container'

const registerStepBackButtonListener = (
  handlers: EventHandlers,
  adapter: EventHandlerAdapter,
) => {
  const goBackButton = document.querySelector<HTMLDivElement>(
    STEP_BACK_BTN_SELECTOR,
  )

  if (goBackButton) {
    goBackButton.onclick = adapter.clickEventToPlayerStepBackward(
      handlers.onPlayerStepBackward,
    )
  }

  const goBackMobileButton = document.querySelector<HTMLDivElement>(
    MOBILE_STEP_BACK_BTN_SELECTOR,
  )

  if (goBackMobileButton) {
    goBackMobileButton.onclick = adapter.clickEventToPlayerStepBackward(
      handlers.onPlayerStepBackward,
    )
  }
}

const registerStepForwardButtonListener = (
  handlers: EventHandlers,
  adapter: EventHandlerAdapter,
) => {
  const goForwardButton = document.querySelector<HTMLDivElement>(
    STEP_FORWARD_BTN_SELECTOR,
  )

  if (goForwardButton) {
    goForwardButton.onclick = adapter.clickEventToPlayerStepForward(
      handlers.onPlayerStepForward,
    )
  }

  const goForwardMobileButton = document.querySelector<HTMLDivElement>(
    MOBILE_STEP_FORWARD_BTN_SELECTOR,
  )

  if (goForwardMobileButton) {
    goForwardMobileButton.onclick = adapter.clickEventToPlayerStepForward(
      handlers.onPlayerStepForward,
    )
  }
}

const registerChapterButtonListener = async (handlers: EventHandlers) => {
  const chapterButton = document.querySelector<HTMLDivElement>(CHAPTER_BUTTON)

  if (chapterButton) {
    chapterButton.onclick = handlers.onChapterClick
  }
}

export const buildGTMTrackingDataFromProduct = (
  currentProduct: Program | ProgramV2 | Trailer,
  autoplay: boolean,
) => {
  const gtmData: GTMProgramMetadata = {
    autoplay,
    productType: currentProduct.productType,
    categories:
      ('categories' in currentProduct && currentProduct.categories) || [],
    format: currentProduct.format,
    id: currentProduct.id,
    mainGenre:
      ('mainGenre' in currentProduct && currentProduct.mainGenre) || '',
    seriesId: ('seriesId' in currentProduct && currentProduct.seriesId) || null,
    seriesTitle:
      ('seriesTitle' in currentProduct && currentProduct.seriesTitle) || null,
    title: currentProduct.title,
    typicalAgeRangeTranslations:
      ('typicalAgeRangeTranslations' in currentProduct &&
        currentProduct.typicalAgeRangeTranslations) ||
      [],
  }

  return gtmData
}

const gtmEventHandler = (
  programInfo: Program | ProgramV2 | Trailer,
  autoplay: boolean,
  sendEvent: (data: EventData) => void,
): Plugin => {
  return (jwPlayer, config) => {
    const gtmData = buildGTMTrackingDataFromProduct(programInfo, autoplay)
    const dataBuilder = new DataBuilder(jwPlayer.getPosition, gtmData)
    const eventPayloadFactory = new EventPayloadFactory(dataBuilder)
    const handlers = new EventHandlers(sendEvent, eventPayloadFactory)
    const adapter = new EventHandlerAdapter(jwPlayer)

    jwPlayer.once('play', handlers.onPlay)
    jwPlayer.on('pause', adapter.onPause(handlers.onPause))
    jwPlayer.on('beforePlay', adapter.beforePlayToResume(handlers.onResume))
    jwPlayer.once('firstFrame', handlers.onFirstFrame)
    timeEventListener(jwPlayer, handlers.onTimeEvent)
    rewindListener(jwPlayer, handlers.onRewind)
    jwPlayer?.on('buffer', handlers.onBuffering)
    abortStreamListener(jwPlayer, handlers.onAbort)
    jwPlayer.on(
      'seek',
      adapter.seekToTimelineDragRelease(handlers.onTimelineDragRelease),
    )
    registerStepBackButtonListener(handlers, adapter)
    registerStepForwardButtonListener(handlers, adapter)

    if (config?.isAudio) {
      registerChapterButtonListener(handlers)
    }

    jwPlayer.on(
      'captionsChanged',
      adapter.onCaptionsChanged(handlers.onCaptionsChanged),
    )
    jwPlayer.on(AUTOPLAY_ABORT, handlers.onAutoPlayAbort)
    jwPlayer.on(AUTOPLAY_CLICK_PLAY, handlers.onAutoPlayClickPlay)
    jwPlayer.on('fullscreen', handlers.onFullscreen)
    jwPlayer.on('visualQuality', handlers.onQualityChange)
  }
}

export default gtmEventHandler
